/**
 * X12 (Lane 3, 2026-09-10; PR #783 corrective round) -- the walk-in source
 * CORRECTION door.
 *
 * Connects the audited correction endpoint (POST /api/walkins/:id/source,
 * `correctWalkinSource`, api/routes/walkins.ts) to the front desk: a
 * receptionist who mis-keyed a walk-in's source at intake can now fix it
 * through the SAME writer + audit trail the backend already builds, in
 * place, next to the recorded source in the Recent walk-ins rail
 * (WalkinActivityPanel). Reuses the ONE source vocabulary owner
 * (WALKIN_SOURCE_OPTIONS, patient-master-view.ts -- the exact set
 * api/routes/walkins.ts SOURCES already validates against) rather than a
 * second copy, and ModalShell (V6 primitive, the SAME centered-work
 * surface grammar every other desk form uses).
 *
 * ITEM-1 CORRECTIVE: the form LOADS the walk-in's existing detail on open
 * (never starts blank) -- an untouched Save resubmits the same value
 * (a true no-op, both persisted and audited); an edited Save replaces it;
 * a deliberately emptied field clears it (the backend's own empty-string-
 * is-a-clear rule).
 *
 * ITEM-3 CORRECTIVE (H3-CORRECTIVE-2): the door carries the FORM-OPEN-TIME
 * baseline (source/sourceDetail/sourceConfidence, captured once when the
 * door opens) and sends it as `expectedSource`/`expectedSourceDetail`/
 * `expectedSourceConfidence` -- the server keys its compare-and-swap on
 * THIS baseline, not a request-time re-read, so a form left open while
 * another correction lands is caught even when the two submissions never
 * overlap in time.
 *
 * ITEM-4 CORRECTIVE: validation copy shows only after an attempt (a Save
 * press or leaving the reason field), never on open before any
 * interaction. "Currently recorded" and the Source picker share ONE
 * label source (WALKIN_SOURCE_OPTIONS). A 409 conflict blocks
 * resubmission against the stale baseline -- the door offers "Reload
 * current values" (re-fetches the true current row, updates the
 * baseline, and shows what changed) while preserving the operator's own
 * in-progress draft untouched, so nothing is silently overwritten in
 * either direction.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): the payload
 * builder, the label resolver and the form-validation predicate are PURE
 * exports, unit-tested directly; the stateful door's CLOSED initial
 * render is pinned by static markup. The open/typing/submit interaction
 * itself is proven by mock-browser (Playwright) evidence against the
 * REAL host page, not simulated DOM events here.
 */

import { useState } from 'react';

import {
  correctWalkinSource,
  fetchWalkinWorkstation,
  type CorrectWalkinSourceBody,
} from '@/dashboard/api';
import { WALKIN_SOURCE_OPTIONS } from '@/dashboard/patients/patient-master-view';
import { AcButton, ModalShell } from '@/dashboard/ui';
import type { WorkstationRecentWalkin } from '@/dashboard/types';
import { humanizeSource } from './ExistingLeadMatches';

export const CORRECT_SOURCE_DOOR_COPY = 'Correct source';
export const CORRECT_SOURCE_REASON_LABEL = 'Reason for correction';
export const CORRECT_SOURCE_REASON_REQUIRED_HINT = 'A reason is required.';
export const CORRECT_SOURCE_SAVE_COPY = 'Save correction';
export const CORRECT_SOURCE_SAVED_COPY = 'Source corrected.';
export const CORRECT_SOURCE_FAILED_COPY = 'Could not save the correction. Try again.';
export const CORRECT_SOURCE_CONFLICT_COPY =
  'This walk-in changed since you opened this form.';
export const CORRECT_SOURCE_RELOAD_COPY = 'Reload current values';
export const CORRECT_SOURCE_RELOADING_COPY = 'Reloading...';
export const CORRECT_SOURCE_RELOAD_FAILED_COPY =
  'Could not reload the current values. Try again.';
export const CORRECT_SOURCE_RELOADED_COPY =
  'Updated to the current recorded value. Review your edit and save again.';
export const CORRECT_SOURCE_CONTINUE_COPY = 'Continue';
// ITEM-1 CORRECTIVE (round 4): audit_failed_unconfirmed is NOT the same
// outcome as a plain save failure -- the walkins row's own compare-and-swap
// DID match and update, but neither the audit write NOR its compensating
// revert could be confirmed, so whether the correction is actually the one
// standing on the row is genuinely unknown from this response alone (see
// api/routes/walkins.ts's own docstring on this outcome). Telling the
// operator "Try again" here would invite a blind resubmission against a
// row whose true state they have not seen.
export const CORRECT_SOURCE_UNCONFIRMED_COPY =
  'Your change may or may not have saved -- the save could not be confirmed either way. Reload to see what is actually on file before trying again.';
export const CORRECT_SOURCE_REASON_MAX_LENGTH = 200;
export const CORRECT_SOURCE_DETAIL_MAX_LENGTH = 120;

export interface SourceCorrectionFormState {
  readonly source: string;
  readonly sourceDetail: string;
  readonly reason: string;
}

export interface SourceBaseline {
  readonly source: string;
  readonly sourceDetail: string | null;
  readonly sourceConfidence: string;
}

/** Pure (item 1/3): the walk-in's CURRENT values, as the door's
 *  form-open-time baseline. */
export function baselineFromWalkin(walkin: WorkstationRecentWalkin): SourceBaseline {
  return {
    source: walkin.source ?? 'walk_in',
    sourceDetail: walkin.sourceDetail ?? null,
    sourceConfidence: walkin.sourceConfidence ?? 'unknown',
  };
}

/** Pure (item 1): the form LOADS the baseline's existing detail -- an
 *  untouched Save resubmits the SAME value (preserves), never a blank
 *  field a save would then clear. */
export function formFromBaseline(baseline: SourceBaseline): SourceCorrectionFormState {
  return { source: baseline.source, sourceDetail: baseline.sourceDetail ?? '', reason: '' };
}

/** Pure: ONE label resolver for a source token, shared by the "Currently
 *  recorded" line and the Source picker (item 4b -- one modal must never
 *  show two different names for the same value). Falls back to the
 *  generic humanizer for a stored value outside the current vocabulary
 *  (a legacy row, never a crash). */
export function sourceLabel(value: string): string {
  return WALKIN_SOURCE_OPTIONS.find((o) => o.value === value)?.label ?? humanizeSource(value);
}

/** Pure: the SAME requiredness/length rules the backend enforces
 *  (validateCorrectSourceBody, api/routes/walkins.ts), checked client-side
 *  first so a caller sees an honest inline message before the round trip
 *  -- the server re-validates regardless and stays the real authority. */
export function validateSourceCorrectionForm(
  form: SourceCorrectionFormState,
): readonly string[] {
  const errors: string[] = [];
  if (!WALKIN_SOURCE_OPTIONS.some((o) => o.value === form.source)) {
    errors.push('Choose a source.');
  }
  if (form.sourceDetail.trim().length > CORRECT_SOURCE_DETAIL_MAX_LENGTH) {
    errors.push('The detail is too long.');
  }
  const reason = form.reason.trim();
  if (reason.length === 0) {
    errors.push(CORRECT_SOURCE_REASON_REQUIRED_HINT);
  } else if (reason.length > CORRECT_SOURCE_REASON_MAX_LENGTH) {
    errors.push('The reason is too long.');
  }
  return errors;
}

/**
 * Pure: form state + the form-open-time baseline -> the request body.
 * The detail field's CURRENT text is always sent as the NEW value (never
 * omitted) -- an empty string reaches the backend's own clear-
 * normalizes-to-null rule, so "leave it blank and save" already IS the
 * supported clearing behaviour. The baseline rides as
 * expectedSource/expectedSourceDetail/expectedSourceConfidence
 * (H3-CORRECTIVE-2) so the server can detect a stale submission instead
 * of trusting a request-time re-read alone.
 */
export function buildCorrectSourceBody(
  form: SourceCorrectionFormState,
  baseline: SourceBaseline,
): CorrectWalkinSourceBody {
  return {
    source: form.source,
    sourceDetail: form.sourceDetail.trim(),
    reason: form.reason.trim(),
    expectedSource: baseline.source,
    expectedSourceDetail: baseline.sourceDetail,
    expectedSourceConfidence: baseline.sourceConfidence,
  };
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string }
  // ITEM-4c: a 409 blocks resubmission against the stale baseline until
  // an explicit reload -- never collapsed into the generic 'failed'
  // copy, which would invite a blind retry against the same stale form.
  | { kind: 'conflict' }
  // ITEM-1 CORRECTIVE (round 4): audit_failed_unconfirmed also blocks
  // resubmission and requires a reload+readback, but carries its OWN
  // copy -- a conflict means SOMEONE ELSE'S change is on the row; an
  // unconfirmed audit means the operator's OWN save might already be on
  // the row, might not be, and neither this response nor a blind retry
  // can tell them which.
  | { kind: 'unconfirmed' }
  | { kind: 'reloading' }
  // ITEM-2 CORRECTIVE (round 4): a failed or empty-row reload must NOT
  // fall back to the generic 'failed' state, which re-enables Save
  // against the very form whose comparison baseline reload just failed
  // to refresh -- it stays retry-blocked, offering only another reload.
  | { kind: 'reload_failed' }
  // ITEM-2 CORRECTIVE (round 4): a SUCCESSFUL reload does not silently
  // re-arm Save -- it shows the newly stored values (via the baseline,
  // which now drives "Currently recorded") and requires one explicit
  // "Continue" before the operator can resubmit, so a detail that
  // changed underneath their unedited draft is never overwritten
  // without having been shown to them first.
  | { kind: 'reloaded' };

function errorCode(err: unknown): string | null {
  if (err !== null && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

function isConflict(err: unknown): boolean {
  const status =
    err !== null && typeof err === 'object' && 'status' in err
      ? (err as { status: unknown }).status
      : null;
  return status === 409;
}

function isUnconfirmedAudit(err: unknown): boolean {
  return errorCode(err) === 'audit_failed_unconfirmed';
}

/** True while the form must stay locked (fields + Save both disabled) and
 *  the ONLY lawful action is reload-and-review -- every SubmitState.kind
 *  except the ones a caller can act through directly (idle/saving/saved/
 *  failed, where 'failed' is a plain save failure the operator may retry
 *  as-is, per item 1's distinction from the unconfirmed/conflict cases). */
function isLocked(kind: SubmitState['kind']): boolean {
  return (
    kind === 'conflict' ||
    kind === 'unconfirmed' ||
    kind === 'reloading' ||
    kind === 'reload_failed' ||
    kind === 'reloaded'
  );
}

export interface WalkinSourceCorrectionDoorProps {
  readonly walkin: WorkstationRecentWalkin;
  /** Fires after a confirmed save so the host can refresh the rail (the
   *  SAME onChanged/reload idiom every other desk mutation door uses). */
  readonly onCorrected?: () => void;
  /** Injected for testability; default wires the real endpoint. */
  readonly correct?: (
    walkinId: number | string,
    body: CorrectWalkinSourceBody,
  ) => ReturnType<typeof correctWalkinSource>;
  /** ITEM-4c: re-fetches this walk-in's TRUE current source/detail/
   *  confidence for the "Reload current values" recovery door. Injected
   *  for testability; default reuses the existing workstation read (the
   *  rail's own data source) and finds this walk-in's row in it -- no
   *  new endpoint. Returns null when the walk-in is no longer present in
   *  that read (e.g. it aged out of the "today" window). */
  readonly reloadWalkin?: (walkinId: number | string) => Promise<SourceBaseline | null>;
}

async function defaultReloadWalkin(walkinId: number | string): Promise<SourceBaseline | null> {
  const data = await fetchWalkinWorkstation();
  const row = data.recentWalkins.find((w) => String(w.id) === String(walkinId));
  return row ? baselineFromWalkin(row) : null;
}

export default function WalkinSourceCorrectionDoor({
  walkin,
  onCorrected,
  correct = correctWalkinSource,
  reloadWalkin = defaultReloadWalkin,
}: WalkinSourceCorrectionDoorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [baseline, setBaseline] = useState<SourceBaseline>(() => baselineFromWalkin(walkin));
  const [form, setForm] = useState<SourceCorrectionFormState>(() => formFromBaseline(baseline));
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  // ITEM-4a: validation copy is honest about WHEN it appears -- never on
  // open before any interaction. Set on the first Save attempt or on
  // leaving the reason field.
  const [showValidation, setShowValidation] = useState(false);

  const openDoor = (): void => {
    const fresh = baselineFromWalkin(walkin);
    setBaseline(fresh);
    setForm(formFromBaseline(fresh));
    setSubmit({ kind: 'idle' });
    setShowValidation(false);
    setOpen(true);
  };
  const closeDoor = (): void => {
    setOpen(false);
  };

  const errors = validateSourceCorrectionForm(form);

  const onSave = (): void => {
    // ROUND-5 CORRECTIVE (item 1): the HANDLER refuses a locked or
    // already-saving state, not just the visible control -- a disabled
    // button is a UI convenience, never the actual guard. Without this,
    // a stray re-entrant call (a fast double-tap racing the render, an
    // enabled control caught by a defect like the 'reloading' one this
    // corrective fixes below) could still fire a second correction
    // request while the first is in flight or the baseline is mid-reload.
    if (submit.kind === 'saving' || isLocked(submit.kind)) return;
    setShowValidation(true);
    if (errors.length > 0) return;
    setSubmit({ kind: 'saving' });
    correct(walkin.id, buildCorrectSourceBody(form, baseline))
      .then(() => {
        setSubmit({ kind: 'saved' });
        onCorrected?.();
        setOpen(false);
      })
      .catch((err: unknown) => {
        if (isConflict(err)) {
          setSubmit({ kind: 'conflict' });
          return;
        }
        // ITEM-1 CORRECTIVE (round 4): checked BEFORE the generic
        // fallback -- audit_failed_unconfirmed is a distinct outcome
        // from a plain save failure (the row's own CAS may have already
        // matched and written; a plain 'failed' copy of "Try again"
        // would invite a blind resubmission against a row whose true
        // state was never shown).
        if (isUnconfirmedAudit(err)) {
          setSubmit({ kind: 'unconfirmed' });
          return;
        }
        setSubmit({ kind: 'failed', message: CORRECT_SOURCE_FAILED_COPY });
      });
  };

  const onReload = (): void => {
    setSubmit({ kind: 'reloading' });
    reloadWalkin(walkin.id)
      .then((fresh) => {
        if (fresh === null) {
          // ITEM-2 CORRECTIVE (round 4): stays retry-blocked -- 'failed'
          // would re-enable Save against a baseline reload just failed
          // to refresh, i.e. the exact stale-overwrite risk a reload was
          // supposed to close.
          setSubmit({ kind: 'reload_failed' });
          return;
        }
        // The baseline updates to the TRUE current row; the operator's
        // own draft (form.source / form.sourceDetail / form.reason) is
        // left exactly as typed -- neither side is silently overwritten.
        // ITEM-2 CORRECTIVE (round 4): this does NOT re-arm Save
        // directly -- 'reloaded' shows the new values (via baseline,
        // now rendered in "Currently recorded") and requires one
        // explicit Continue, so a changed detail is never silently
        // carried over by an unedited draft field.
        setBaseline(fresh);
        setSubmit({ kind: 'reloaded' });
      })
      .catch(() => {
        setSubmit({ kind: 'reload_failed' });
      });
  };

  // ITEM-2 CORRECTIVE (round 4): the one explicit acknowledgement gate
  // between "reload showed the new values" and "the form is editable and
  // Save is live again" -- required review before resubmission.
  const onContinueAfterReload = (): void => {
    setSubmit({ kind: 'idle' });
  };

  const currentLabel = sourceLabel(baseline.source);

  return (
    <>
      <button
        type="button"
        className="recent-row__correct-source"
        onClick={openDoor}
        data-testid={`walkin-correct-source-${walkin.id}`}
      >
        {CORRECT_SOURCE_DOOR_COPY}
      </button>
      {open ? (
        <ModalShell
          title="Correct walk-in source"
          onClose={closeDoor}
          testId="walkin-source-correction-modal"
          footer={
            <>
              <AcButton variant="ghost" onClick={closeDoor}>
                Cancel
              </AcButton>
              {submit.kind === 'reloading' ? (
                // ROUND-5 CORRECTIVE (item 1): 'reloading' fell through
                // to the Save branch below, rendering an ENABLED Save
                // button while a reload was in flight -- Save must stay
                // blocked for the WHOLE recovery, not just the states
                // either side of it. No action is lawful mid-reload (not
                // Save; not a second Reload, which would race the first);
                // this button is disabled and named for what is actually
                // happening.
                <AcButton
                  variant="primary"
                  disabled
                  data-testid="walkin-correct-source-reload"
                >
                  {CORRECT_SOURCE_RELOADING_COPY}
                </AcButton>
              ) : submit.kind === 'conflict' ||
                submit.kind === 'unconfirmed' ||
                submit.kind === 'reload_failed' ? (
                <AcButton
                  variant="primary"
                  onClick={onReload}
                  data-testid="walkin-correct-source-reload"
                >
                  {CORRECT_SOURCE_RELOAD_COPY}
                </AcButton>
              ) : submit.kind === 'reloaded' ? (
                <AcButton
                  variant="primary"
                  onClick={onContinueAfterReload}
                  data-testid="walkin-correct-source-continue"
                >
                  {CORRECT_SOURCE_CONTINUE_COPY}
                </AcButton>
              ) : (
                <AcButton
                  variant="primary"
                  onClick={onSave}
                  disabled={(showValidation && errors.length > 0) || submit.kind === 'saving'}
                  data-testid="walkin-correct-source-save"
                >
                  {submit.kind === 'saving' ? 'Saving...' : CORRECT_SOURCE_SAVE_COPY}
                </AcButton>
              )}
            </>
          }
        >
          {/* ITEM-2 CORRECTIVE (round 4): shows the baseline's DETAIL too,
              not only its source -- previously a reload updated this
              line's source but nothing surfaced the current DETAIL, so a
              detail that changed underneath an untouched draft field was
              invisible right up to the point a blind Save would have
              overwritten it. */}
          <p className="correct-source__current" data-testid="walkin-correct-source-current">
            Currently recorded: <strong>{currentLabel}</strong>
            {baseline.sourceDetail ? (
              <>
                {' -- '}
                <strong data-testid="walkin-correct-source-current-detail">
                  {baseline.sourceDetail}
                </strong>
              </>
            ) : null}
          </p>
          <label className="correct-source__field">
            Source
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              disabled={isLocked(submit.kind)}
              data-testid="walkin-correct-source-select"
            >
              {WALKIN_SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="correct-source__field">
            Detail (optional)
            <input
              type="text"
              value={form.sourceDetail}
              maxLength={CORRECT_SOURCE_DETAIL_MAX_LENGTH}
              disabled={isLocked(submit.kind)}
              onChange={(e) => setForm({ ...form, sourceDetail: e.target.value })}
              data-testid="walkin-correct-source-detail"
            />
          </label>
          <label className="correct-source__field">
            {CORRECT_SOURCE_REASON_LABEL}
            <textarea
              value={form.reason}
              maxLength={CORRECT_SOURCE_REASON_MAX_LENGTH}
              disabled={isLocked(submit.kind)}
              onBlur={() => setShowValidation(true)}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="walkin-correct-source-reason"
            />
          </label>
          {showValidation && errors.length > 0 ? (
            <p className="correct-source__error" role="alert" data-testid="walkin-correct-source-validation-error">
              {errors[0]}
            </p>
          ) : null}
          {submit.kind === 'failed' ? (
            <p className="correct-source__error" role="alert" data-testid="walkin-correct-source-failed">
              {submit.message}
            </p>
          ) : null}
          {submit.kind === 'conflict' ? (
            <p
              className="correct-source__error"
              role="alert"
              data-testid="walkin-correct-source-conflict"
            >
              {CORRECT_SOURCE_CONFLICT_COPY} Your edit is kept below -- reload to see the
              current recorded value, then review before saving again.
            </p>
          ) : null}
          {submit.kind === 'unconfirmed' ? (
            <p
              className="correct-source__error"
              role="alert"
              data-testid="walkin-correct-source-unconfirmed"
            >
              {CORRECT_SOURCE_UNCONFIRMED_COPY} Your edit is kept below -- reload to see what
              is actually saved, then review before trying again.
            </p>
          ) : null}
          {submit.kind === 'reload_failed' ? (
            <p
              className="correct-source__error"
              role="alert"
              data-testid="walkin-correct-source-reload-failed"
            >
              {CORRECT_SOURCE_RELOAD_FAILED_COPY} Your edit is kept below; try reloading
              again before saving.
            </p>
          ) : null}
          {submit.kind === 'reloading' ? (
            <p className="correct-source__hint" role="status">
              {CORRECT_SOURCE_RELOADING_COPY}
            </p>
          ) : null}
          {submit.kind === 'reloaded' ? (
            <p
              className="correct-source__hint"
              role="status"
              data-testid="walkin-correct-source-reloaded"
            >
              {CORRECT_SOURCE_RELOADED_COPY} Now on file: <strong>{currentLabel}</strong>
              {baseline.sourceDetail ? (
                <>
                  {' -- '}
                  <strong>{baseline.sourceDetail}</strong>
                </>
              ) : null}
              . Your own draft below is unchanged -- press Continue to review and save it
              against these values.
            </p>
          ) : null}
        </ModalShell>
      ) : null}
    </>
  );
}
